"use client";

import {
  FC,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { usePostHog } from "posthog-js/react";
import {
  FeatureFlagsContext,
  IFeatureFlagsContextValue,
} from "libs/hooks/src/lib/featureFlagsContext";

// How long to wait for posthog before unblocking the UI without flags.
const FLAGS_READY_TIMEOUT_MS = 3000;

interface IFeatureFlagsProviderProps {
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Provides resolved posthog feature flag variants to descendants via context.
 *
 * Lifecycle:
 *   1. Mounts with `ready: false` and renders `fallback` (typically a spinner).
 *   2. On the first `posthog.onFeatureFlags` callback, flips `ready: true`
 *      and renders children with the resolved variants.
 *   3. If posthog doesn't respond within FLAGS_READY_TIMEOUT_MS, a safety
 *      timer flips `ready: true` anyway so the UI isn't stuck on the fallback.
 *
 * IMPORTANT — only consume device-id-bucketed flags through this provider.
 * Posthog evaluates flags against `$device_id` on first load, then re-evaluates
 * after `posthog.identify(userId)`. User-id bucketed flags can flip variant
 * mid-session, changing UI under an in-progress flow and splitting analytics.
 * Device-id bucketing stays stable across `identify()`.
 */
export const FeatureFlagsProvider: FC<IFeatureFlagsProviderProps> = ({
  fallback = null,
  children,
}) => {
  const posthog = usePostHog();
  const [ value, setValue ] = useState<IFeatureFlagsContextValue>({ ready: false, flags: {} });

  useEffect(() => {
    // Tracks whether this component is still mounted. The cleanup function
    // flips it to false, and every async callback below checks it before
    // calling setValue — otherwise we'd update state on a component that no
    // longer exists.
    let isMounted = true;

    // Safety timer: if posthog never responds, flip `ready: true` after the
    // timeout so users aren't stuck looking at the fallback forever.
    const timeoutId = setTimeout(() => {
      if (!isMounted) { return; }

      console.warn(
        `[FeatureFlagsProvider] flags didn't load within ${FLAGS_READY_TIMEOUT_MS}ms — unblocking UI without them`,
      );
      // Use the functional setter so we don't overwrite any flags posthog may
      // have pushed in the meantime — we only want to unblock rendering.
      setValue((prev) => (prev.ready ? prev : { ready: true, flags: prev.flags }));
    }, FLAGS_READY_TIMEOUT_MS);

    if (posthog) {
      posthog.onFeatureFlags((_keys, variants, context) => {
        if (!isMounted) { return; }
        // Flags arrived (success or failure), so cancel the safety timer.
        clearTimeout(timeoutId);
        if (context?.errorsLoading) {
          console.warn("[FeatureFlagsProvider] posthog reported errorsLoading", context);
        }
        setValue({
          ready: true,
          flags: variants ?? {},
        });
      });
    }

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [ posthog ]);

  if (!value.ready) {
    return <>{ fallback }</>;
  }

  return (
    <FeatureFlagsContext.Provider value={value}>
      { children }
    </FeatureFlagsContext.Provider>
  );
};
